<template>
  <pp-dialog :active="active" :max-width="520" @update:active="$emit('update:active', $event)">
    <div v-if="page" class="pp-share" :style="{ '--pp-color': companyColor }">
      <span v-if="isPublished" class="pp-share__status pp-share__status--on">
        <check-icon size="14" /> Seu perfil está no ar
      </span>
      <span v-else class="pp-share__status">Seu perfil está fora do ar</span>
      <h3 class="pp-share__title">Compartilhe seu perfil</h3>

      <!-- Grátis com nome livre: mostra os dois links na hora de escolher o que mandar. -->
      <template v-if="showNameChoice">
        <p class="pp-share__subtitle">Qual link você vai mandar?</p>
        <div class="pp-share__choices">
          <div class="pp-share__choice pp-share__choice--active">
            <span class="pp-share__dot" />
            <span class="pp-share__choice-text">
              <strong>{{ shortUrl }}</strong>
              <small>Seu link de agora, no plano grátis</small>
            </span>
            <button type="button" class="pp-share__copy pp-share__copy--small" @click="copyLink">
              <copy-icon size="14" />
              {{ copied ? "Copiado" : "Copiar" }}
            </button>
          </div>
          <div class="pp-share__choice pp-share__choice--pro">
            <div class="pp-share__choice-head">
              <span class="pp-share__dot" />
              <span class="pp-share__choice-text">
                <span class="pp-share__choice-url">
                  <strong>{{ suggestedUrl }}</strong>
                  <span class="pp-share__free">Livre</span>
                  <pro-badge />
                </span>
                <small>{{ reservationText }}</small>
              </span>
            </div>
            <button type="button" class="pp-btn pp-btn--pro pp-share__claim" @click="claimName">
              <globe-icon size="16" />
              Garantir meu nome por {{ lifetimePrice }}, uma vez só
            </button>
          </div>
        </div>
        <p class="pp-share__hint">Os botões abaixo mandam o link marcado.</p>
      </template>
      <template v-else>
        <slot name="address" />

        <div class="pp-share__link-row">
          <div class="pp-share__link">
            <link-icon size="16" />
            <span>{{ shortUrl }}</span>
          </div>
          <button type="button" class="pp-share__copy" @click="copyLink">
            <copy-icon size="16" />
            {{ copied ? "Copiado" : "Copiar link" }}
          </button>
        </div>
      </template>

      <div class="pp-share__channels">
        <a
          v-for="channel in channels"
          :key="channel.id"
          class="pp-share__channel"
          :class="`pp-share__channel--${channel.id}`"
          :href="channel.href"
          target="_blank"
          rel="noopener noreferrer"
          @click="onChannel(channel)"
        >
          <span class="pp-share__channel-icon">
            <component :is="channel.icon" size="22" />
          </span>
          {{ channel.label }}
        </a>
        <button
          type="button"
          class="pp-share__channel"
          :class="{ 'pp-share__channel--active': showQr }"
          @click="toggleQr"
        >
          <span class="pp-share__channel-icon"><grid-icon size="22" /></span>
          QR code
        </button>
      </div>

      <div v-if="showQr" class="pp-share__qr">
        <img v-if="qrDataUrl" :src="qrDataUrl" alt="QR code do seu perfil público" />
        <p v-else-if="qrError" class="pp-share__muted">Não foi possível gerar o QR code.</p>
        <a
          v-if="qrDataUrl"
          :href="qrDataUrl"
          :download="`perfil-${page.path}-qrcode.png`"
          class="pp-share__qr-download"
        >
          <download-icon size="16" /> Baixar QR code
        </a>
      </div>

      <hr class="pp-share__divider" />

      <p class="pp-share__subtitle">Quem pode ver</p>
      <div class="pp-share__options">
        <button
          type="button"
          class="pp-share__option"
          :class="{ 'pp-share__option--active': isPublished }"
          :disabled="busy"
          @click="setPublished(true)"
        >
          <span class="pp-share__radio" />
          <span>
            <strong>Qualquer pessoa com o link</strong>
            <small>Recrutadores e contatos que você mandar</small>
          </span>
        </button>
        <button
          type="button"
          class="pp-share__option"
          :class="{ 'pp-share__option--active': !isPublished }"
          :disabled="busy"
          @click="setPublished(false)"
        >
          <span class="pp-share__radio" />
          <span>
            <strong>Ninguém, por enquanto</strong>
            <small>O link fica desativado até você ligar de novo</small>
          </span>
        </button>
      </div>

      <div class="pp-share__google">
        <div>
          <strong>Aparecer em buscas do Google</strong>
          <small>
            {{
              searchIndexing
                ? "Ligado: seu perfil pode aparecer nas buscas pelo seu nome."
                : "Desligado: só quem tem o link encontra seu perfil."
            }}
          </small>
        </div>
        <vs-switch :color="companyColor" :value="searchIndexing" @input="setSearchIndexing" />
      </div>

      <button type="button" class="pp-share__pdf" @click="openPdf">
        <download-icon size="20" />
        <span>
          <strong>Baixar em PDF</strong>
          <small>Versão para imprimir ou anexar, com o link do perfil</small>
        </span>
      </button>

      <!-- Discreto de propósito: quem só quer tirar do ar usa "Ninguém, por enquanto". -->
      <button
        v-if="!confirmDelete"
        type="button"
        class="pp-share__delete-link"
        @click="askDelete"
      >
        Excluir perfil público
      </button>
      <div v-else class="pp-share__delete">
        <strong>Excluir seu perfil público de vez?</strong>
        <p>
          O link para de funcionar e as mensagens e visitas do perfil são apagadas. Não dá para
          desfazer. Seu currículo e seus dados na Recrutei continuam como estão.
        </p>
        <div class="pp-share__delete-actions">
          <button type="button" class="pp-btn" :disabled="busy" @click="cancelDelete">
            Cancelar
          </button>
          <button
            type="button"
            class="pp-btn pp-share__delete-confirm"
            :disabled="busy"
            @click="deleteProfile"
          >
            Excluir de vez
          </button>
        </div>
      </div>
    </div>
  </pp-dialog>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import {
  CheckIcon,
  LinkIcon,
  CopyIcon,
  MessageCircleIcon,
  LinkedinIcon,
  MailIcon,
  GridIcon,
  DownloadIcon,
  GlobeIcon,
} from "vue-feather-icons";
import PpDialog from "./PpDialog.vue";
import ProBadge from "./ProBadge.vue";
import mixin from "./mixin";
import { trackPublicProfile } from "./tracking";
import { copyText, displayUrl, formatCents, formatDate } from "./constants";

// Mesmo formato do Compartilhar da página pública (recrutei-profile-front, ShareButton).
// O link copiado fica sem UTM: a pessoa cola em qualquer lugar e link limpo passa mais confiança.
function withUtm(url, source) {
  if (!url) return url;
  try {
    const next = new URL(url);
    next.searchParams.set("utm_source", source);
    next.searchParams.set("utm_medium", "compartilhar");
    return next.toString();
  } catch (e) {
    return url;
  }
}
import { errorReason, errorStatus, pdfUrl } from "@/services/publicProfile";

export default {
  name: "ShareDialog",
  components: {
    PpDialog,
    CheckIcon,
    LinkIcon,
    CopyIcon,
    MessageCircleIcon,
    LinkedinIcon,
    MailIcon,
    GridIcon,
    DownloadIcon,
    GlobeIcon,
    ProBadge,
  },
  mixins: [mixin],
  props: {
    active: { type: Boolean, default: false },
    source: { type: String, default: "editor" },
  },
  data: () => ({
    copied: false,
    showQr: false,
    qrDataUrl: "",
    qrError: false,
    busy: false,
    confirmDelete: false,
  }),
  computed: {
    ...mapState("publicProfile", ["draft"]),
    ...mapGetters("publicProfile", [
      "page",
      "isPublished",
      "isPro",
      "needsPro",
      "proTemplates",
      "proSections",
      "prices",
    ]),
    // Nome livre que a API guarda para quem ainda não é PRO (suggested_slug).
    showNameChoice() {
      return !this.isPro && !!this.page && !this.page.slug && !!this.page.suggested_slug;
    },
    suggestedUrl() {
      const host = this.shortUrl.split("/")[0] || "recrutei.me";
      return `${host}/${this.page.suggested_slug}`;
    },
    reservationText() {
      const until = formatDate(this.page && this.page.suggested_slug_until).slice(0, 5);
      return until
        ? `Guardamos esse nome para você até ${until}. Depois disso, outra pessoa pode ficar com ele.`
        : "Esse nome ainda está livre.";
    },
    lifetimePrice() {
      return formatCents(this.prices.lifetime_cents);
    },
    // Perfil ja no ar: vale o que esta salvo, nao a demonstracao do rascunho
    // (ela nunca vai para o servidor). Fora do ar, publicar leva o rascunho.
    publishNeedsPro() {
      if (!this.isPublished) return this.needsPro;
      if (this.isPro || !this.page) return false;
      const page = this.page;
      if (this.proTemplates.includes(page.template)) return true;
      if ((page.sections || []).some((s) => s.visible && this.proSections.includes(s.id))) return true;
      return ["hide_branding", "hide_resume_download", "hide_share"].some((flag) => !!page[flag]);
    },
    pageUrl() {
      return (this.page && this.page.url) || "";
    },
    shortUrl() {
      return displayUrl(this.page && this.page.url);
    },
    searchIndexing() {
      if (this.draft) return !!this.draft.search_indexing;
      return !!(this.page && this.page.search_indexing);
    },
    channels() {
      const url = (this.page && this.page.url) || "";
      // Cada canal leva a UTM dele (igual ao Compartilhar da página pública): sem isso, quem
      // abre pelo WhatsApp chega sem referrer e o Analytics mostra "Direto ou app".
      const text = (source) => `Meu perfil profissional: ${withUtm(url, source)}`;
      return [
        {
          id: "whatsapp",
          label: "WhatsApp",
          icon: "message-circle-icon",
          href: `https://wa.me/?text=${encodeURIComponent(text("whatsapp"))}`,
        },
        {
          id: "linkedin",
          label: "LinkedIn",
          icon: "linkedin-icon",
          href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(withUtm(url, "linkedin"))}`,
        },
        {
          id: "email",
          label: "E-mail",
          icon: "mail-icon",
          href: `mailto:?subject=${encodeURIComponent("Meu perfil profissional")}&body=${encodeURIComponent(text("email"))}`,
        },
      ];
    },
  },
  watch: {
    active(value) {
      if (value) {
        this.ppTrack("public_profile_share_dialog_viewed", {
          source: this.source,
          name_offer_shown: this.showNameChoice,
        });
      }
      if (!value) {
        this.showQr = false;
        this.copied = false;
        this.confirmDelete = false;
      }
    },
    // Mudou o endereco (nome novo, virou PRO): o QR antigo apontaria para o link velho.
    pageUrl() {
      this.showQr = false;
      this.qrDataUrl = "";
      this.qrError = false;
    },
  },
  methods: {
    async copyLink() {
      const ok = await copyText(this.page.url);
      if (ok) {
        this.copied = true;
        setTimeout(() => (this.copied = false), 2000);
        this.ppTrack("public_profile_link_copied", { source: `share_${this.source}` });
      } else {
        this.ppError("Não deu para copiar. Selecione o link e copie à mão.");
      }
    },
    claimName() {
      this.ppTrack("public_profile_share_name_claim_clicked", {
        source: this.source,
        has_reservation: !!(this.page && this.page.suggested_slug_until),
      });
      this.$emit("pro-required", "share_slug");
    },
    onChannel(channel) {
      this.ppTrack("public_profile_shared", { channel: channel.id, source: this.source });
    },
    async toggleQr() {
      this.showQr = !this.showQr;
      this.ppTrack("public_profile_qrcode_toggled", { open: this.showQr, source: this.source });
      if (!this.showQr || this.qrDataUrl) return;
      this.ppTrack("public_profile_shared", { channel: "qrcode", source: this.source });
      try {
        // Gerado no navegador: o link nao passa por servico de terceiros.
        const mod = await import(/* webpackChunkName: "qrcode" */ "qrcode");
        const QRCode = mod.default || mod;
        const url = this.page.url;
        const dataUrl = await QRCode.toDataURL(withUtm(url, "qrcode"), { width: 240, margin: 1 });
        // O endereco pode ter mudado enquanto gerava.
        if (url === this.pageUrl) this.qrDataUrl = dataUrl;
      } catch (e) {
        this.qrError = true;
      }
    },
    async setPublished(value) {
      if (this.busy || value === this.isPublished) return;
      this.ppTrack("public_profile_publish_toggle_clicked", {
        publish: value,
        source: this.source,
        needs_pro: !!(value && this.publishNeedsPro),
      });
      if (value && this.publishNeedsPro) {
        this.$emit("pro-required");
        return;
      }
      this.busy = true;
      try {
        if (value) {
          await this.$store.dispatch("publicProfile/publish");
          this.ppTrack("public_profile_published", { source: `share_${this.source}` }, { setPerson: true });
          this.ppNotify("Seu perfil está no ar.");
        } else {
          await this.$store.dispatch("publicProfile/unpublish");
          this.ppTrack("public_profile_unpublished", { source: `share_${this.source}` }, { setPerson: true });
          this.ppNotify("Pronto, o link ficou desativado.");
        }
      } catch (error) {
        this.ppTrack(value ? "public_profile_publish_failed" : "public_profile_unpublish_failed", {
          source: `share_${this.source}`,
          status: errorStatus(error),
          reason: errorReason(error),
        });
        if (errorStatus(error) === 402 || errorReason(error) === "pro_required") {
          this.$emit("pro-required");
        } else {
          this.ppError();
        }
      } finally {
        this.busy = false;
      }
    },
    async deleteProfile() {
      if (this.busy) return;
      this.busy = true;
      try {
        // Copia antes do destroy: depois dele o store fica vazio e o evento sairia sem o perfil.
        const before = JSON.parse(JSON.stringify(this.$store.state.publicProfile));
        await this.$store.dispatch("publicProfile/destroy");
        trackPublicProfile(before, "public_profile_deleted", {
          source: this.source,
          $set: { public_profile_deleted: true, public_profile_is_published: false },
        });
        this.ppNotify("Seu perfil público foi excluído.");
        this.confirmDelete = false;
        this.$emit("update:active", false);
        this.$emit("deleted");
      } catch (error) {
        this.ppTrack("public_profile_delete_failed", {
          source: this.source,
          status: errorStatus(error),
          reason: errorReason(error),
        });
        if (errorReason(error) === "subscription_active") {
          this.ppError("Sua assinatura mensal ainda está ativa. Cancele em Gerenciar assinatura e depois exclua o perfil.");
        } else {
          this.ppError();
        }
      } finally {
        this.busy = false;
      }
    },
    setSearchIndexing(value) {
      this.$store.dispatch("publicProfile/edit", { search_indexing: !!value, _track: { origin: `share_${this.source}` } });
    },
    askDelete() {
      this.ppTrack("public_profile_delete_clicked", { source: this.source });
      this.confirmDelete = true;
    },
    cancelDelete() {
      this.ppTrack("public_profile_delete_canceled", { source: this.source });
      this.confirmDelete = false;
    },
    async openPdf() {
      this.ppTrack("public_profile_pdf_opened", { source: `share_${this.source}` });
      // Abre a aba ja no clique (bloqueador de pop-up) e so depois salva.
      const tab = window.open("", "_blank");
      await this.$store.dispatch("publicProfile/flush").catch(() => {});
      const url = pdfUrl(this.page.path);
      if (tab) tab.location.href = url;
      else window.open(url, "_blank");
    },
  },
};
</script>

<style lang="scss">
.pp-share {
  display: flex;
  flex-direction: column;
  font-family: Roboto, sans-serif;

  &__status {
    display: inline-flex;
    align-self: flex-start;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border: 0.5px solid #a8a8a8;
    border-radius: 4px;
    background: #ecebeb;
    color: #4f4f4f;
    font-size: 12px;
    font-weight: 500;

    &--on {
      border-color: #86efac;
      background: #dcfce7;
      color: #166534;
    }
  }

  &__title {
    margin: 8px 40px 20px 0;
    color: #333;
    font-size: 22px;
    font-weight: 600;
  }

  &__link-row {
    display: flex;
    gap: 8px;
  }

  &__choices {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__choice {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 12px 12px 16px;
    border: 0.5px solid #a8a8a8;
    border-radius: 8px;

    &--active {
      border: 1px solid var(--pp-color);
    }

    &--pro {
      flex-direction: column;
      align-items: stretch;
      padding: 14px 16px;
      border: 1px dashed #6554c0;
      background: rgba(101, 84, 192, 0.06);
    }
  }

  &__choice-head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  &__dot {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border: 1px solid #a8a8a8;
    border-radius: 50%;
    background: #fff;

    .pp-share__choice--active & {
      border: 5px solid var(--pp-color);
    }
  }

  &__choice-text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    strong {
      overflow: hidden;
      color: #333;
      font-size: 15px;
      font-weight: 500;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    small {
      color: #696969;
      font-size: 13px;
      line-height: 1.35;
    }
  }

  &__choice-url {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  &__free {
    padding: 2px 6px;
    border-radius: 4px;
    background: #dcfce7;
    color: #166534;
    font-size: 11px;
    font-weight: 500;
  }

  &__claim {
    width: 100%;
    margin-top: 12px;
  }

  &__hint {
    margin: 12px 0 4px;
    color: #696969;
    font-size: 13px;
  }

  &__link {
    display: flex;
    flex: 1;
    align-items: center;
    gap: 8px;
    min-width: 0;
    height: 44px;
    padding: 0 12px;
    border: 0.5px solid #a8a8a8;
    border-radius: 8px;
    color: #4f4f4f;
    font-size: 16px;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    svg {
      flex-shrink: 0;
    }
  }

  &__copy {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 8px;
    height: 44px;
    padding: 0 16px;
    border: none;
    border-radius: 8px;
    background: var(--pp-color);
    color: #fff;
    font-family: Roboto, sans-serif;
    font-size: 16px;
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
      filter: brightness(0.92);
    }

    &--small {
      gap: 6px;
      height: 36px;
      padding: 0 12px;
      font-size: 14px;
    }
  }

  &__channels {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-top: 20px;
  }

  &__channel {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    border: none;
    background: none;
    color: #4f4f4f;
    font-family: Roboto, sans-serif;
    font-size: 12px;
    text-decoration: none;
    cursor: pointer;

    &:hover .pp-share__channel-icon {
      filter: brightness(0.95);
    }

    &--whatsapp .pp-share__channel-icon {
      background: #25d366;
      color: #fff;
    }

    &--active .pp-share__channel-icon {
      box-shadow: 0 0 0 2px var(--pp-color);
    }
  }

  &__channel-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 9999px;
    background: #ecebeb;
    color: #333;
  }

  &__qr {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    margin-top: 16px;

    img {
      width: 180px;
      height: 180px;
    }
  }

  &__qr-download {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #4f4f4f;
    font-size: 14px;
    text-decoration: underline;
  }

  &__muted {
    color: #696969;
    font-size: 14px;
  }

  &__divider {
    margin: 20px 0;
    border: none;
    border-top: 0.5px solid #a8a8a8;
  }

  &__subtitle {
    margin-bottom: 12px;
    color: #333;
    font-size: 16px;
    font-weight: 500;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__option {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border: 0.5px solid #a8a8a8;
    border-radius: 8px;
    background: #fff;
    font-family: Roboto, sans-serif;
    text-align: left;
    cursor: pointer;

    strong {
      display: block;
      color: #333;
      font-size: 16px;
      font-weight: 500;
    }

    small {
      color: #696969;
      font-size: 13px;
    }

    &:disabled {
      cursor: wait;
      opacity: 0.7;
    }

    &--active {
      border-color: var(--pp-color);
      box-shadow: 0 0 0 0.5px var(--pp-color);

      .pp-share__radio {
        border-color: var(--pp-color);

        &::after {
          content: "";
          position: absolute;
          top: 3px;
          left: 3px;
          width: 10px;
          height: 10px;
          border-radius: 9999px;
          background: var(--pp-color);
        }
      }
    }
  }

  &__radio {
    position: relative;
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border: 1px solid #a8a8a8;
    border-radius: 9999px;
  }

  &__google {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 20px;

    strong {
      display: block;
      color: #333;
      font-size: 16px;
      font-weight: 500;
    }

    small {
      color: #696969;
      font-size: 13px;
    }
  }

  &__pdf {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 20px;
    padding: 16px;
    border: none;
    border-radius: 8px;
    background: #ecebeb;
    color: #333;
    font-family: Roboto, sans-serif;
    text-align: left;
    cursor: pointer;

    strong {
      display: block;
      font-size: 16px;
      font-weight: 500;
    }

    small {
      color: #696969;
      font-size: 13px;
    }

    &:hover {
      background: #e7edf0;
    }
  }

  &__delete-link {
    align-self: center;
    margin-top: 16px;
    padding: 4px;
    border: 0;
    background: none;
    color: #8a8a8a;
    font-size: 12px;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: #c62828;
    }
  }

  &__delete {
    margin-top: 16px;
    padding: 12px 16px;
    border: 1px solid #f5c2c2;
    border-radius: 8px;
    background: #fff5f5;
    color: #4f4f4f;
    font-size: 13px;

    strong {
      color: #333;
      font-size: 14px;
    }

    p {
      margin: 4px 0 12px;
      line-height: 1.4;
    }
  }

  &__delete-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  &__delete-confirm {
    border-color: #c62828;
    background: #c62828;
    color: #fff;
  }
}
</style>
